import gql from "graphql-tag";
import * as React from "react";

import type {AsyncApolloQueryResult} from "@repo/data-gql-apollo";
import {gqlOp, type GqlOperation} from "@repo/data-gql-core";

import ApolloQuery from "#data-access/gql/apollo-query.tsx";

export type Target = {
    id: string;
    nickname: string | null | undefined;
    username: string | null | undefined;
    isActor: boolean;
    isChild: boolean;
    email: string | null | undefined;
    isEnrolledInAIGuide: boolean;
};

type SettingsPageQueryResult = {
    target: Target | null | undefined;
    emailToUnsubscribe: string | null | undefined;
    isLoading: boolean;
    isError: boolean;
};

type Data = {
    target?: Target;
    emailSettingsFromUnsubscribeToken?: {
        email: string | null | undefined;
    };
};

type Variables = {
    targetKaid?: string | null | undefined;
    token?: string | null | undefined;
};

type SettingsPageQueryResponse = AsyncApolloQueryResult<Data, Variables>;

type Props = {
    targetKaid: string | null | undefined;
    token: string | null | undefined;
    children: (result: SettingsPageQueryResult) => React.ReactNode;
};

export class SettingsPageQuery extends React.Component<Props> {
    render(): React.ReactNode {
        const {targetKaid, token, children} = this.props;

        if (token) {
            return (
                <ApolloQuery
                    query={DATA_QUERY_WITH_TOKEN}
                    variables={{targetKaid, token}}
                    fetchPolicy="cache-and-network"
                >
                    {(result) => children(mapDataToResult(result))}
                </ApolloQuery>
            );
        }

        return (
            <ApolloQuery
                query={DATA_QUERY}
                variables={{targetKaid}}
                fetchPolicy="cache-and-network"
            >
                {(result) => children(mapDataToResult(result))}
            </ApolloQuery>
        );
    }
}

const mapDataToResult = ({
    data,
    error,
    loading,
}: SettingsPageQueryResponse): SettingsPageQueryResult => {
    const isError = error != null;

    const maybeEmailToUnsubscribe =
        data?.emailSettingsFromUnsubscribeToken?.email;

    if (loading || !data?.target) {
        return {
            isError,
            isLoading: loading,
            target: null,
            emailToUnsubscribe: maybeEmailToUnsubscribe,
        };
    }

    return {
        isError,
        isLoading: loading,
        target: data.target,
        emailToUnsubscribe: maybeEmailToUnsubscribe,
    };
};

const DATA_QUERY: GqlOperation<Data, Variables> = gqlOp(gql`
    query getSettingsPageData($targetKaid: String) {
        target: user(kaid: $targetKaid) {
            id
            nickname
            username
            isActor
            isChild
            email
            isEnrolledInAIGuide
        }
    }
`);

const DATA_QUERY_WITH_TOKEN: GqlOperation<Data, Variables> = gqlOp(gql`
    query getSettingsPageDataByToken($token: String!, $targetKaid: String) {
        target: user(kaid: $targetKaid) {
            id
            nickname
            username
            isActor
            isChild
            email
            isEnrolledInAIGuide
        }
        emailSettingsFromUnsubscribeToken(token: $token) {
            email
        }
    }
`);

export const DATA_QUERY_FOR_TEST = DATA_QUERY;
export const DATA_QUERY_WITH_TOKEN_FOR_TEST = DATA_QUERY_WITH_TOKEN;
